import { useState, useEffect } from 'react';
import { quotes, clients } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, FileText, Send, Check, X, ArrowRight, Trash2, MoreVertical, Eye, 
  DollarSign, TrendingUp, Clock, CheckCircle2, XCircle, AlertTriangle, Printer
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import QuotePrint from '@/components/QuotePrint';

const statusLabels = {
  draft: 'Borrador',
  pending_approval: 'Pendiente Aprobación',
  approved: 'Aprobada',
  sent: 'Enviada',
  accepted: 'Aceptada',
  rejected: 'Rechazada',
  converted: 'Convertida',
};

const statusColors = {
  draft: 'bg-slate-500/10 text-slate-600',
  pending_approval: 'bg-amber-500/10 text-amber-600',
  approved: 'bg-emerald-500/10 text-emerald-600',
  sent: 'bg-blue-500/10 text-blue-600',
  accepted: 'bg-emerald-500/10 text-emerald-600',
  rejected: 'bg-rose-500/10 text-rose-600',
  converted: 'bg-primary/10 text-primary',
};

function CreateQuoteDialog({ open, onOpenChange, onSubmit, clientsList }) {
  const [formData, setFormData] = useState({
    client_id: '',
    client_email: '',
    notes: '',
    valid_until: '',
    items: [{ description: '', quantity: 1, unit_price: 0, unit_cost: 0 }],
  });

  const selectedClient = clientsList.find(c => c.client_id === formData.client_id);

  useEffect(() => {
    if (selectedClient?.email) {
      setFormData(prev => ({ ...prev, client_email: selectedClient.email }));
    }
  }, [selectedClient]);

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0, unit_cost: 0 }],
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = ['quantity', 'unit_price', 'unit_cost'].includes(field) 
      ? parseFloat(value) || 0 
      : value;
    setFormData({ ...formData, items: newItems });
  };

  // Revenue calculations
  const subtotal = formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  const tax = subtotal * 0.18;
  const total = subtotal + tax;

  // Cost calculations (internal)
  const totalCost = formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_cost), 0);
  const grossProfit = subtotal - totalCost;
  const profitMargin = subtotal > 0 ? (grossProfit / subtotal * 100) : 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      client_id: formData.client_id,
      client_email: formData.client_email,
      items: formData.items.filter(item => item.description),
      notes: formData.notes,
    };
    if (formData.valid_until) {
      data.valid_until = new Date(formData.valid_until).toISOString();
    }
    onSubmit(data);
    setFormData({
      client_id: '',
      client_email: '',
      notes: '',
      valid_until: '',
      items: [{ description: '', quantity: 1, unit_price: 0, unit_cost: 0 }],
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Nueva Cotización</DialogTitle>
          <DialogDescription>Complete los datos de la cotización incluyendo costos internos</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cliente *</Label>
              <Select value={formData.client_id} onValueChange={(val) => setFormData({...formData, client_id: val})}>
                <SelectTrigger data-testid="quote-client-select">
                  <SelectValue placeholder="Seleccionar cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clientsList.map((client) => (
                    <SelectItem key={client.client_id} value={client.client_id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Email del Cliente *</Label>
              <Input
                type="email"
                value={formData.client_email}
                onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                placeholder="cliente@empresa.com"
                required
              />
              <p className="text-xs text-muted-foreground">Se enviará la cotización a este email</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Válido hasta</Label>
            <Input
              type="date"
              value={formData.valid_until}
              onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
            />
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label>Items</Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem}>
                <Plus className="h-4 w-4 mr-1" /> Agregar
              </Button>
            </div>
            
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-[30%]">Descripción</TableHead>
                    <TableHead className="w-[12%] text-center">Cant.</TableHead>
                    <TableHead className="w-[18%] text-right">Precio Unit.</TableHead>
                    <TableHead className="w-[18%] text-right bg-amber-50">Costo Unit. (Interno)</TableHead>
                    <TableHead className="w-[15%] text-right">Subtotal</TableHead>
                    <TableHead className="w-[7%]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Input
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          placeholder="Descripción del servicio/producto"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          className="text-center"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                          className="text-right"
                        />
                      </TableCell>
                      <TableCell className="bg-amber-50/50">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_cost}
                          onChange={(e) => updateItem(index, 'unit_cost', e.target.value)}
                          className="text-right"
                          placeholder="0"
                        />
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(item.quantity * item.unit_price)}
                      </TableCell>
                      <TableCell>
                        {formData.items.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)}>
                            <Trash2 className="h-4 w-4 text-rose-500" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Client-facing totals */}
            <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-600">Totales para el Cliente</p>
              <div className="flex justify-between text-sm">
                <span>Subtotal:</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>ITBIS (18%):</span>
                <span>{formatCurrency(tax)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Total:</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>

            {/* Internal profit analysis */}
            <div className="space-y-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm font-medium text-amber-800 flex items-center gap-1">
                <TrendingUp className="h-4 w-4" />
                Análisis Interno (No visible al cliente)
              </p>
              <div className="flex justify-between text-sm">
                <span>Ingresos:</span>
                <span className="text-emerald-600">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Costos:</span>
                <span className="text-rose-600">{formatCurrency(totalCost)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold border-t pt-2">
                <span>Utilidad Bruta:</span>
                <span className={grossProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                  {formatCurrency(grossProfit)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Margen:</span>
                <Badge variant={profitMargin >= 20 ? 'success' : profitMargin >= 10 ? 'warning' : 'destructive'} 
                       className={profitMargin >= 20 ? 'bg-emerald-100 text-emerald-700' : profitMargin >= 10 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}>
                  {profitMargin.toFixed(1)}%
                </Badge>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Notas</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Notas adicionales..."
              rows={2}
            />
          </div>
          
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!formData.client_id || !formData.client_email || formData.items.every(i => !i.description)}>
              Crear Cotización
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ApprovalDialog({ open, onOpenChange, quote, onApprove, onReject }) {
  const [rejectionReason, setRejectionReason] = useState('');
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  if (!quote) return null;

  const handleApprove = async () => {
    setApproving(true);
    await onApprove(quote.quote_id);
    setApproving(false);
    onOpenChange(false);
  };

  const handleReject = async () => {
    setRejecting(true);
    await onReject(quote.quote_id, rejectionReason);
    setRejecting(false);
    setRejectionReason('');
    onOpenChange(false);
  };

  const grossProfit = quote.gross_profit || 0;
  const profitMargin = quote.profit_margin || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Aprobar Cotización {quote.quote_number}
          </DialogTitle>
          <DialogDescription>Revise los detalles financieros antes de aprobar</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Quote Info */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Cliente:</span>
                <p className="font-medium">{quote.client_name}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Email:</span>
                <p className="font-medium">{quote.client_email}</p>
              </div>
            </div>
          </div>

          {/* Financial Summary */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="pt-4 text-center">
                <DollarSign className="h-8 w-8 mx-auto text-emerald-600 mb-2" />
                <p className="text-sm text-emerald-700">Ingresos</p>
                <p className="text-2xl font-bold text-emerald-700">{formatCurrency(quote.subtotal)}</p>
              </CardContent>
            </Card>
            <Card className="border-rose-200 bg-rose-50">
              <CardContent className="pt-4 text-center">
                <TrendingUp className="h-8 w-8 mx-auto text-rose-600 mb-2 rotate-180" />
                <p className="text-sm text-rose-700">Costos</p>
                <p className="text-2xl font-bold text-rose-700">{formatCurrency(quote.total_cost || 0)}</p>
              </CardContent>
            </Card>
            <Card className={`border-2 ${grossProfit >= 0 ? 'border-emerald-500 bg-emerald-100' : 'border-rose-500 bg-rose-100'}`}>
              <CardContent className="pt-4 text-center">
                <TrendingUp className={`h-8 w-8 mx-auto mb-2 ${grossProfit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`} />
                <p className={`text-sm ${grossProfit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>Utilidad Bruta</p>
                <p className={`text-2xl font-bold ${grossProfit >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {formatCurrency(grossProfit)}
                </p>
                <Badge className={`mt-1 ${profitMargin >= 20 ? 'bg-emerald-600' : profitMargin >= 10 ? 'bg-amber-500' : 'bg-rose-600'}`}>
                  Margen: {profitMargin.toFixed(1)}%
                </Badge>
              </CardContent>
            </Card>
          </div>

          {/* Items Detail */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Descripción</TableHead>
                  <TableHead className="text-center">Cant.</TableHead>
                  <TableHead className="text-right">Precio</TableHead>
                  <TableHead className="text-right">Costo</TableHead>
                  <TableHead className="text-right">Utilidad</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quote.items?.map((item, idx) => {
                  const itemRevenue = item.quantity * item.unit_price;
                  const itemCost = item.quantity * (item.unit_cost || 0);
                  const itemProfit = itemRevenue - itemCost;
                  return (
                    <TableRow key={idx}>
                      <TableCell>{item.description}</TableCell>
                      <TableCell className="text-center">{item.quantity}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                      <TableCell className="text-right text-rose-600">{formatCurrency(item.unit_cost || 0)}</TableCell>
                      <TableCell className={`text-right font-medium ${itemProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {formatCurrency(itemProfit)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Total with tax */}
          <div className="p-4 bg-slate-100 rounded-lg">
            <div className="flex justify-between text-sm">
              <span>Subtotal:</span>
              <span>{formatCurrency(quote.subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>ITBIS (18%):</span>
              <span>{formatCurrency(quote.tax)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t mt-2 pt-2">
              <span>Total a Cobrar:</span>
              <span>{formatCurrency(quote.total)}</span>
            </div>
          </div>

          {/* Rejection reason */}
          <div className="space-y-2">
            <Label>Razón de rechazo (opcional)</Label>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Si rechaza, indique el motivo..."
              rows={2}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={rejecting}
            >
              <XCircle className="h-4 w-4 mr-2" />
              {rejecting ? 'Rechazando...' : 'Rechazar'}
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleApprove}
              disabled={approving}
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              {approving ? 'Aprobando...' : 'Aprobar y Enviar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Quotes() {
  const { user } = useAuth();
  const [quotesList, setQuotesList] = useState([]);
  const [clientsList, setClientsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [quotesRes, clientsRes] = await Promise.all([
        quotes.getAll(),
        clients.getAll(),
      ]);
      setQuotesList(quotesRes.data);
      setClientsList(clientsRes.data);
    } catch (err) {
      console.error('Error loading quotes:', err);
      toast.error('Error al cargar cotizaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      await quotes.create(data);
      toast.success('Cotización creada');
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error('Error al crear cotización');
    }
  };

  const openPrintDialog = (quote) => {
    setSelectedQuote(quote);
    setPrintDialogOpen(true);
  };

  const handleRequestApproval = async (quoteId) => {
    try {
      await quotes.requestApproval(quoteId);
      toast.success('Cotización enviada para aprobación');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al solicitar aprobación');
    }
  };

  const handleApprove = async (quoteId) => {
    try {
      const res = await quotes.approve(quoteId, true);
      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error('Error al aprobar cotización');
    }
  };

  const handleReject = async (quoteId, reason) => {
    try {
      await quotes.reject(quoteId, reason);
      toast.success('Cotización rechazada');
      loadData();
    } catch (err) {
      toast.error('Error al rechazar cotización');
    }
  };

  const handleConvert = async (quoteId) => {
    try {
      await quotes.convert(quoteId);
      toast.success('Cotización convertida a factura');
      loadData();
    } catch (err) {
      toast.error('Error al convertir cotización');
    }
  };

  const openApprovalDialog = (quote) => {
    setSelectedQuote(quote);
    setApprovalDialogOpen(true);
  };

  // Filter quotes
  const pendingApprovalQuotes = quotesList.filter(q => q.approval_status === 'pending');
  const filteredQuotes = activeTab === 'pending' 
    ? pendingApprovalQuotes 
    : quotesList;

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedQuotes = [...filteredQuotes].sort((a, b) => {
    let valA, valB;
    switch (sortField) {
      case 'client_name':
        valA = (a.client_name || '').toLowerCase();
        valB = (b.client_name || '').toLowerCase();
        break;
      case 'total':
        valA = a.total || 0;
        valB = b.total || 0;
        break;
      default:
        valA = a.created_at || '';
        valB = b.created_at || '';
    }
    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Stats
  const totalQuotes = quotesList.length;
  const pendingCount = pendingApprovalQuotes.length;
  const totalRevenue = quotesList.filter(q => q.status === 'accepted' || q.status === 'converted')
    .reduce((sum, q) => sum + q.total, 0);
  const totalProfit = quotesList.filter(q => q.status === 'accepted' || q.status === 'converted')
    .reduce((sum, q) => sum + (q.gross_profit || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="quotes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">Cotizaciones</h1>
          <p className="text-muted-foreground">Gestión de propuestas con análisis de rentabilidad</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="create-quote-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nueva Cotización
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Cotizaciones</p>
                <p className="text-2xl font-bold">{totalQuotes}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {isAdmin && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-amber-700">Pendientes Aprobación</p>
                  <p className="text-2xl font-bold text-amber-700">{pendingCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Ingresos Aceptados</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totalRevenue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {isAdmin && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Utilidad Bruta</p>
                  <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totalProfit)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabs for Admin */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">Todas</TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="pending" className="relative">
              Por Aprobar
              {pendingCount > 0 && (
                <Badge className="ml-2 bg-amber-500">{pendingCount}</Badge>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('created_at')} data-testid="sort-quote-number">
                      Número {sortField === 'created_at' && (sortDir === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('client_name')} data-testid="sort-quote-client">
                      Cliente {sortField === 'client_name' && (sortDir === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('total')} data-testid="sort-quote-total">
                      Total {sortField === 'total' && (sortDir === 'asc' ? '↑' : '↓')}
                    </TableHead>
                    {isAdmin && <TableHead className="text-right">Utilidad</TableHead>}
                    {isAdmin && <TableHead className="text-center">Margen</TableHead>}
                    <TableHead>Estado</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedQuotes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 8 : 6} className="text-center text-muted-foreground py-8">
                        No hay cotizaciones
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedQuotes.map((quote) => (
                      <TableRow key={quote.quote_id}>
                        <TableCell className="font-medium">{quote.quote_number}</TableCell>
                        <TableCell>
                          <div>
                            <p>{quote.client_name}</p>
                            {quote.client_email && (
                              <p className="text-xs text-muted-foreground">{quote.client_email}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(quote.total)}</TableCell>
                        {isAdmin && (
                          <TableCell className={`text-right font-mono ${(quote.gross_profit || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {formatCurrency(quote.gross_profit || 0)}
                          </TableCell>
                        )}
                        {isAdmin && (
                          <TableCell className="text-center">
                            <Badge className={`${(quote.profit_margin || 0) >= 20 ? 'bg-emerald-100 text-emerald-700' : (quote.profit_margin || 0) >= 10 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
                              {(quote.profit_margin || 0).toFixed(1)}%
                            </Badge>
                          </TableCell>
                        )}
                        <TableCell>
                          <Badge className={statusColors[quote.status]}>
                            {statusLabels[quote.status]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(quote.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            {/* Print/View button - always visible */}
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => openPrintDialog(quote)}
                              title="Ver/Imprimir"
                            >
                              <Printer className="h-4 w-4" />
                            </Button>

                            {quote.status === 'draft' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleRequestApproval(quote.quote_id)}
                              >
                                <Send className="h-4 w-4 mr-1" />
                                Solicitar Aprobación
                              </Button>
                            )}
                            
                            {isAdmin && quote.approval_status === 'pending' && (
                              <Button 
                                size="sm" 
                                className="bg-amber-500 hover:bg-amber-600"
                                onClick={() => openApprovalDialog(quote)}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Revisar
                              </Button>
                            )}

                            {(quote.status === 'sent' || quote.status === 'accepted') && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {quote.status === 'sent' && (
                                    <>
                                      <DropdownMenuItem onClick={() => quotes.updateStatus(quote.quote_id, 'accepted').then(loadData)}>
                                        <Check className="h-4 w-4 mr-2 text-emerald-500" /> Marcar Aceptada
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => quotes.updateStatus(quote.quote_id, 'rejected').then(loadData)}>
                                        <X className="h-4 w-4 mr-2 text-rose-500" /> Marcar Rechazada
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  {quote.status === 'accepted' && (
                                    <DropdownMenuItem onClick={() => handleConvert(quote.quote_id)}>
                                      <ArrowRight className="h-4 w-4 mr-2" /> Convertir a Factura
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <CreateQuoteDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmit={handleCreate}
        clientsList={clientsList}
      />

      <ApprovalDialog
        open={approvalDialogOpen}
        onOpenChange={setApprovalDialogOpen}
        quote={selectedQuote}
        onApprove={handleApprove}
        onReject={handleReject}
      />

      <QuotePrint
        quote={selectedQuote}
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
      />
    </div>
  );
}
